import { useEffect, useState } from "react";
import axios from "axios";
import {
  Car,
  Fuel,
  Gauge,
  Calendar,
  Cog,
  AlertTriangle,
  ArrowRight,
  Phone,
  X as CloseIcon,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatPrice(n) {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(n || 0);
}

function formatKm(n) {
  return new Intl.NumberFormat("fr-FR").format(n || 0) + " km";
}

function StatusBadge({ statut }) {
  const map = {
    Disponible: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
    Réservé: "bg-amber-500/10 text-amber-600 border-amber-500/20",
    Vendu: "bg-slate-900/10 text-slate-700 border-slate-300",
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider border ${
        map[statut] || map.Disponible
      }`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {statut}
    </span>
  );
}

function VehicleCard({ v, onOpen }) {
  const cover = v.photos?.[0];
  return (
    <article
      data-testid={`vehicle-card-${v.id}`}
      onClick={() => onOpen(v)}
      className={`group cursor-pointer bg-white border border-slate-200 rounded-lg overflow-hidden lift hover:border-blue-300 hover:shadow-xl ${
        v.statut === "Vendu" ? "opacity-75" : ""
      }`}
    >
      <div className="relative aspect-[4/3] bg-slate-100 overflow-hidden">
        {cover ? (
          <img
            src={cover}
            alt={`${v.marque} ${v.modele}`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full grid place-items-center text-slate-300">
            <Car size={64} strokeWidth={1} />
          </div>
        )}
        <div className="absolute top-3 left-3">
          <StatusBadge statut={v.statut} />
        </div>
        {v.statut === "Vendu" && (
          <div className="absolute inset-0 grid place-items-center bg-slate-900/40">
            <span className="font-display font-black text-white text-3xl tracking-wider border-4 border-white px-6 py-2 rotate-[-8deg]">
              VENDU
            </span>
          </div>
        )}
      </div>

      <div className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h3 className="font-display font-black text-lg text-slate-900 leading-tight">
              {v.marque} {v.modele}
            </h3>
            {v.version && (
              <div className="text-sm text-slate-500 mt-0.5">{v.version}</div>
            )}
          </div>
          <div className="font-display font-black text-xl text-blue-600 whitespace-nowrap">
            {formatPrice(v.prix)}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 text-xs text-slate-600 mb-4">
          <Spec icon={Calendar} label={v.annee} />
          <Spec icon={Gauge} label={formatKm(v.kilometrage)} />
          <Spec icon={Fuel} label={v.energie} />
          <Spec icon={Cog} label={v.boite} />
        </div>

        <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 group-hover:gap-2.5 transition-all">
          Voir le détail
          <ArrowRight size={14} />
        </div>
      </div>
    </article>
  );
}

function Spec({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <Icon size={14} className="text-slate-400 shrink-0" />
      <span className="truncate">{label}</span>
    </div>
  );
}

function VehicleModal({ vehicle, onClose }) {
  const [idx, setIdx] = useState(0);
  const photos = vehicle?.photos || [];

  useEffect(() => {
    setIdx(0);
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight" && photos.length > 1)
        setIdx((i) => (i + 1) % photos.length);
      if (e.key === "ArrowLeft" && photos.length > 1)
        setIdx((i) => (i - 1 + photos.length) % photos.length);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [vehicle, photos.length, onClose]);

  if (!vehicle) return null;

  return (
    <div
      data-testid="vehicle-modal"
      className="fixed inset-0 z-[100] bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg max-w-5xl w-full max-h-[92vh] overflow-hidden flex flex-col lg:flex-row"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative lg:w-3/5 bg-slate-100 aspect-[4/3] lg:aspect-auto">
          {photos.length > 0 ? (
            <>
              <img
                src={photos[idx]}
                alt={`${vehicle.marque} ${vehicle.modele}`}
                className="w-full h-full object-cover"
              />
              {photos.length > 1 && (
                <>
                  <button
                    data-testid="vehicle-modal-prev"
                    onClick={() =>
                      setIdx((i) => (i - 1 + photos.length) % photos.length)
                    }
                    className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 grid place-items-center rounded-full bg-white/90 hover:bg-white text-slate-900 shadow-lg"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    data-testid="vehicle-modal-next"
                    onClick={() => setIdx((i) => (i + 1) % photos.length)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 grid place-items-center rounded-full bg-white/90 hover:bg-white text-slate-900 shadow-lg"
                  >
                    <ChevronRight size={20} />
                  </button>
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {photos.map((_, i) => (
                      <button
                        key={`photo-dot-${i}`}
                        onClick={() => setIdx(i)}
                        className={`h-1.5 rounded-full transition-all ${
                          i === idx ? "w-8 bg-white" : "w-1.5 bg-white/50"
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="w-full h-full grid place-items-center text-slate-300">
              <Car size={96} strokeWidth={1} />
            </div>
          )}
          <button
            data-testid="vehicle-modal-close"
            onClick={onClose}
            className="absolute top-3 right-3 h-10 w-10 grid place-items-center rounded-full bg-white/90 hover:bg-white text-slate-900 shadow-lg lg:hidden"
          >
            <CloseIcon size={20} />
          </button>
        </div>

        <div className="lg:w-2/5 p-6 md:p-8 overflow-y-auto relative">
          <button
            data-testid="vehicle-modal-close-desktop"
            onClick={onClose}
            className="hidden lg:grid absolute top-4 right-4 h-9 w-9 place-items-center rounded-full hover:bg-slate-100 text-slate-600"
          >
            <CloseIcon size={18} />
          </button>
          <StatusBadge statut={vehicle.statut} />
          <h3 className="mt-3 font-display font-black text-2xl md:text-3xl text-slate-900 leading-tight">
            {vehicle.marque} {vehicle.modele}
          </h3>
          {vehicle.version && (
            <div className="text-slate-500 mt-1">{vehicle.version}</div>
          )}
          <div className="font-display font-black text-3xl text-blue-600 mt-4">
            {formatPrice(vehicle.prix)}
          </div>

          <dl className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-slate-200">
            <Field label="Année" value={vehicle.annee} />
            <Field label="Kilométrage" value={formatKm(vehicle.kilometrage)} />
            <Field label="Énergie" value={vehicle.energie} />
            <Field label="Boîte" value={vehicle.boite} />
          </dl>

          {vehicle.description && (
            <div className="mt-6 pt-6 border-t border-slate-200">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">
                Description
              </div>
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                {vehicle.description}
              </p>
            </div>
          )}

          <a
            href="tel:+33649580664"
            data-testid="vehicle-modal-call"
            className="mt-6 inline-flex items-center justify-center gap-2 w-full rounded-md bg-blue-600 hover:bg-blue-700 px-5 py-3 text-sm font-semibold text-white lift"
          >
            <Phone size={16} /> Appeler le garage — 06 49 58 06 64
          </a>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <dt className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
        {label}
      </dt>
      <dd className="mt-1 font-semibold text-slate-900">{value}</dd>
    </div>
  );
}

export default function Vehicles() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/vehicles`)
      .then((res) => !cancelled && setVehicles(res.data || []))
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const available = vehicles.filter((v) => v.statut !== "Vendu");
  const sold = vehicles.filter((v) => v.statut === "Vendu").slice(0, 3);

  let listContent;
  if (loading) {
    listContent = (
      <div className="text-center text-slate-500 py-20">Chargement…</div>
    );
  } else if (available.length === 0 && sold.length === 0) {
    listContent = (
      <div className="bg-white border border-dashed border-slate-300 rounded-lg p-10 text-center">
        <Car size={48} className="mx-auto text-slate-300 mb-4" strokeWidth={1.5} />
        <h3 className="font-display font-bold text-xl text-slate-900 mb-2">
          Aucun véhicule en vente actuellement
        </h3>
        <p className="text-slate-600 text-sm max-w-md mx-auto">
          Revenez bientôt, de nouveaux véhicules sont régulièrement ajoutés.
          En attendant, n'hésitez pas à nous contacter pour nous faire part
          de votre recherche.
        </p>
        <a
          href="tel:+33649580664"
          className="mt-5 inline-flex items-center gap-2 rounded-md bg-blue-600 hover:bg-blue-700 px-5 py-2.5 text-sm font-semibold text-white"
        >
          <Phone size={14} /> 06 49 58 06 64
        </a>
      </div>
    );
  } else {
    listContent = (
      <>
        {available.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {available.map((v) => (
              <VehicleCard key={v.id} v={v} onOpen={setSelected} />
            ))}
          </div>
        )}
        {sold.length > 0 && (
          <div className="mt-12">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-4">
              Récemment vendus
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {sold.map((v) => (
                <VehicleCard key={v.id} v={v} onOpen={setSelected} />
              ))}
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <section
      id="vehicules"
      data-testid="vehicles-section"
      className="py-24 lg:py-32 bg-slate-50"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-14">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-4">
            Véhicules d'occasion
          </div>
          <h2 className="font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tighter text-slate-900">
            Nos véhicules <br />
            <span className="text-slate-500">en vente.</span>
          </h2>
        </div>

        {listContent}

        {/* Reprise block */}
        <div
          data-testid="reprise-block"
          className="mt-16 relative overflow-hidden bg-slate-950 text-white rounded-lg p-8 md:p-12"
        >
          <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-blue-600/20 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-amber-500/10 blur-3xl pointer-events-none" />

          <div className="relative grid md:grid-cols-[auto_1fr] gap-6 md:gap-8 items-start">
            <div className="h-14 w-14 shrink-0 grid place-items-center rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-400">
              <AlertTriangle size={24} strokeWidth={2} />
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400 mb-3">
                Reprise de véhicule
              </div>
              <h3 className="font-display font-black text-3xl md:text-4xl tracking-tight mb-5 leading-tight">
                Votre véhicule a besoin de réparations ?<br />
                <span className="text-slate-400">Nous le reprenons.</span>
              </h3>
              <p className="text-slate-300 text-[15px] leading-relaxed max-w-2xl mb-4">
                Nous reprenons <strong className="text-white">uniquement les véhicules avec des frais à prévoir</strong> :
              </p>
              <ul className="space-y-2 mb-6 text-slate-200">
                <li className="flex items-start gap-3">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-400 mt-2 shrink-0" />
                  <span>Véhicules dont le <strong>contrôle technique ne passe pas</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-400 mt-2 shrink-0" />
                  <span>Véhicules avec <strong>moteur ou boîte de vitesse cassés</strong></span>
                </li>
              </ul>
              <p className="text-slate-400 text-sm max-w-2xl mb-6">
                Contactez-nous par téléphone pour évaluer la reprise de votre
                véhicule. Offre juste et transparente selon l'état réel.
              </p>
              <a
                href="tel:+33649580664"
                data-testid="reprise-cta-phone"
                className="inline-flex items-center gap-2 rounded-md bg-white hover:bg-slate-100 px-5 py-3 text-sm font-semibold text-slate-900 lift"
              >
                <Phone size={16} /> Nous contacter — 06 49 58 06 64
              </a>
            </div>
          </div>
        </div>
      </div>

      {selected && <VehicleModal vehicle={selected} onClose={() => setSelected(null)} />}
    </section>
  );
}
